import { Media, MediaResponse, MediaUploadRequest, MediaUploadResponse } from '../types/Media';
import { client } from '../utils/fetch';
import { supabase } from '../utils/supabaseClient';

const API_ENDPOINT = 'images';

// Fetch images list from the bucket with pagination and sorting
export const getImagesList = async (
  bucketName: string,
  fpath: string,
  limit: number,
  offset: number,
  sortBy: string = 'last_modified',
  sortOrder: 'asc' | 'desc' = 'desc',
  search?: string
): Promise<MediaResponse> => {
  try {
    const params = new URLSearchParams({
      bucket_name: bucketName,
      fpath: fpath,
      limit: limit.toString(),
      offset: offset.toString(),
      sort_by: sortBy,
      sort_order: sortOrder,
    });

    if (search && search.trim()) {
      params.append('search', search);
    }

    const response = await client.get<MediaResponse>(`${API_ENDPOINT}?${params.toString()}`);
    
    if (response.images && response.images.length > 0) {
      response.images.sort((a, b) => {
        if (sortBy === 'last_modified') {
          const dateA = new Date(a.last_modified).getTime();
          const dateB = new Date(b.last_modified).getTime();
          return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        }
        return 0;
      });
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching images list:', error);
    throw error;
  }
};

// Get authenticated image URL using the user's own JWT
export const getAuthenticatedImageUrl = async (
  imageUrl: string
): Promise<string> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching authenticated image:', error);
    throw error;
  }
};

// Complete workflow: fetch images list and return with authenticated URL helper
export const getMediaLibrary = async (
  bucketName: string,
  fpath: string,
  limit: number,
  offset: number,
  sortBy: string = 'last_modified',
  sortOrder: 'asc' | 'desc' = 'desc',
  search?: string
): Promise<MediaResponse> => {
  try {
    return await getImagesList(bucketName, fpath, limit, offset, sortBy, sortOrder, search);
  } catch (error) {
    console.error('Error in media library fetch:', error);
    throw error;
  }
};

export const searchImages = async (
  query: string,
  bucketName: string = 'display-images',
  fpath: string = 'base-images',
  limit: number = 20,
  offset: number = 0
): Promise<Media[]> => {
  try {
    const response = await getImagesList(bucketName, fpath, limit, offset, 'last_modified', 'desc', query);
    return response.images || [];
  } catch (error) {
    console.error('Error searching images:', error);
    throw error;
  }
};

export const addMedia = async (mediaData: MediaUploadRequest): Promise<MediaUploadResponse> => {
  try {
    // Create FormData for file upload
    const formData = new FormData();
    
    // Add files to form data
    mediaData.files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add other fields
    formData.append('bucket_name', mediaData.bucket_name);
    formData.append('fpath', mediaData.fpath);

    // Use client.post with FormData
    const result = await client.post<MediaUploadResponse>(`${API_ENDPOINT}/upload`, formData);
    
    return result;
  } catch (error) {
    console.error("Error adding media:", error);
    throw error;
  }
};





  